import { useCallback, useMemo } from 'react';

import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { BenefitProposalProps } from '~/data/proposals';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  CommonProposalFieldProps,
  EmployeeProposalDecision,
  EmployeeProposalUpdateCallback,
  UseCommonProposalFormOutput,
  commonEnumOptionFiltering,
  transformProposalRequestProps,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { useCommonFieldProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useCommonFieldProps';
import { useInitiateFormData } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useInitiateFormData';
import { useProposalPermissions } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalPermissions';
import { getEmployeeAttachmentUrl } from '~/utils/helper';

import { useEmploymentEnumsFilter } from '../Common/useEmploymentEnumsFilter';
import { useScheduleEmployments } from '../Common/useScheduleEmployments';

import {
  BenefitProposalFormData,
  mapDefaultData,
  mapFormDataToLegacyPayload,
  submitBenefitProposal,
  useValidationSchema,
} from './data';

type PropsType = {
  proposal: BenefitProposalProps;
  employeeId: number;
  onProposalUpdate: EmployeeProposalUpdateCallback;
};

type UseBenefitProposalFormOutput =
  UseCommonProposalFormOutput<BenefitProposalFormData> & {
    enums: {
      benefitTypes: Enum[];
      eventFrequencies: Enum[];
    };
    fieldProps: CommonProposalFieldProps;
    attachmentUrl: string | undefined;
  };

export const useBenefitProposalForm = (
  props: PropsType,
): UseBenefitProposalFormOutput => {
  const { proposal, employeeId, onProposalUpdate } = props;
  const { proposalData } = proposal;

  const {
    readOnlyMode,
    userRoles,
    loggedInEmployeeId,
    canSubmitForm,
    responsibleHRSelectEnabled,
  } = useProposalPermissions({ proposal });

  const { hasUpcomingEmployments } = useScheduleEmployments();

  const { filterActiveEnums } = useEmploymentEnumsFilter();

  const resolver = useYupValidationResolver(
    useValidationSchema(hasUpcomingEmployments),
    'react-hook-form',
  );

  //TODO: NEW ENDPOINT MISSING ATTACHMENT
  const {
    allResponsibleHRIds,
    isLoading,
    form,
    handleFormReset,
    handleSubmitRequest,
    quickFixEnsureAttachmentFileInPayload,
  } = useInitiateFormData<BenefitProposalProps, BenefitProposalFormData>({
    proposal,
    employeeId,
    dataMapper: mapDefaultData,
    resolver,
  });

  const { getEnums } = useAllEnums();
  const enums = useMemo(() => {
    const { filterEnumsByCodes, rejectCodes } = commonEnumOptionFiltering;

    const getEnumOptions = (
      listKey: EnumListKey,
      selectedValue?: string,
    ): Enum[] => filterActiveEnums(getEnums(listKey), selectedValue);
    return {
      benefitTypes: getEnumOptions(
        EnumListKey.BENEFIT_TYPE,
        proposalData?.benefitTypeFK,
      ),
      eventFrequencies: filterEnumsByCodes(
        getEnumOptions(EnumListKey.EVENT_FREQUENCY, proposalData?.frequencyFK),
        rejectCodes.EVENT_FREQUENCY,
      ),
    };
  }, [
    proposalData?.benefitTypeFK,
    proposalData?.frequencyFK,
    filterActiveEnums,
    getEnums,
  ]);

  const { watch, getValues } = form;
  const handleFormSubmit = useCallback(
    (decision?: EmployeeProposalDecision) => {
      const attachmentFile = getValues('attachment');

      return handleSubmitRequest({
        onSuccess: onProposalUpdate,
        handleSubmission: (data, requestProps) => {
          requestProps = transformProposalRequestProps(
            requestProps,
            +data.employmentId,
          );
          return submitBenefitProposal(
            mapFormDataToLegacyPayload({
              data: quickFixEnsureAttachmentFileInPayload(data, attachmentFile),
              isHRUser: userRoles.isHR,
              loggedInEmployeeId,
              decision,
              requestProps,
            }),
            requestProps,
          );
        },
      });
    },
    [
      handleSubmitRequest,
      onProposalUpdate,
      userRoles,
      loggedInEmployeeId,
      quickFixEnsureAttachmentFileInPayload,
      getValues,
    ],
  );

  const { isLoading: isLoadingCommonFieldProps, ...fieldProps } =
    useCommonFieldProps({
      startDate: { value: watch('startDate') },
      endDate: { value: watch('endDate') },
      employmentId: watch('employmentId'),
    });

  const attachmentId = watch('attachmentId');
  const attachmentUrl = useMemo(() => {
    return !!attachmentId && attachmentId > 0
      ? getEmployeeAttachmentUrl(employeeId, attachmentId)
      : undefined;
  }, [employeeId, attachmentId]);

  return {
    form,
    enums,
    handleFormSubmit,
    handleFormReset,
    responsibleHRSelector: {
      options: allResponsibleHRIds,
      isResponsibleHR: userRoles.isResponsibleHR,
      enabled: responsibleHRSelectEnabled,
    },
    isLoading: {
      responsibleHR: isLoading,
      commonFieldProps: isLoadingCommonFieldProps,
    },
    readOnlyMode,
    canSubmitForm,
    fieldProps,
    attachmentUrl,
  };
};
